import { signalSummary } from '../../logic/signalCounts';
import type { IsoDate, Signal } from '../../../../shared/types';
import { NetworkError } from '../../data/api';
import { loadNewSignals } from '../../data/signals';
import type { StoredSignals } from '../../data/signals';
import type { Period } from '../../logic/home';
import { newBusinessSummary } from '../../logic/overview';
import { SOURCE_NAMES } from '../../logic/signals';
import { empty, section } from '../../ui/components';
import { h, replaceChildren, span } from '../../ui/dom';
import { kindIcon } from '../../ui/icons';
import { SIGNAL_KIND_LABELS } from '../../ui/labels';
import { cardLink } from '../home/cardLink';

const bizRow = (s: Signal): HTMLElement =>
  h(
    'a',
    { class: 'bizrow', href: `#/signals?id=${encodeURIComponent(s.id)}` },
    kindIcon(s.kind),
    h(
      'span',
      { class: 'bizrow__text' },
      span('bizrow__name', s.business_name),
      span('bizrow__meta', [s.city, SIGNAL_KIND_LABELS[s.kind]].filter(Boolean).join(' · ')),
    ),
    span('srctag', SOURCE_NAMES[s.source]),
  );

/** The businesses most worth a visit (D57, D72), from the stored list when offline (D65). Loads once
 *  per visit; the Week / Month switch only recounts. */
export const businessCard = (): {
  el: HTMLElement;
  update: (period: Period, today: IsoDate) => void;
  dispose: () => void;
} => {
  const body = h('div', { class: 'ovbiz' }, h('p', { class: 'card__summary' }, 'Checking…'));
  const el = section({ title: 'New businesses', action: cardLink('See all', '#/signals') }, body);
  let alive = true;
  let data: (StoredSignals & { offline: boolean }) | null = null;
  let period: Period = 'week';
  let today = '';

  const paint = () => {
    if (!data || !today) return;
    const all = data.list.signals;
    const { count, top } = newBusinessSummary(all, today, period);
    const worth = all.filter((s) => s.bucket === 'top').length;
    const when = period === 'week' ? 'this week' : 'this month';
    replaceChildren(
      body,
      h(
        'p',
        { class: 'card__summary' },
        [
          `${count} new ${when}`,
          worth ? `${worth} worth a visit` : null,
          `${signalSummary(data.list).total} to review`,
          data.offline ? 'offline' : null,
        ]
          .filter(Boolean)
          .join(' · '),
      ),
      top.length ? top.map(bizRow) : empty("You're caught up."),
    );
  };

  loadNewSignals()
    .then((d) => {
      if (!alive) return;
      data = d;
      paint();
    })
    .catch((err: unknown) => {
      if (!alive) return;
      replaceChildren(
        body,
        h(
          'p',
          { class: 'card__summary' },
          err instanceof NetworkError ? 'Needs a connection to check.' : 'Could not load new businesses.',
        ),
      );
    });

  return {
    el,
    update: (p, t) => {
      period = p;
      today = t;
      paint();
    },
    dispose: () => {
      alive = false;
    },
  };
};
